export const DBO_AUTODISCOVERY_VERSION = '0.50.0';
export const DBO_CONNECTION_TRACKING_VERSION = '0.62.1';
export const DBO_CONNECTION_TRACKING_VERSION_MYSQL = '0.75.0';

export const dboUrlSearchKeys = {
  roxy: 'roxy',
};

export const dboUrlKeys = {
  cacheGroupId: 'cacheGroupId',
  cacheConfigId: 'cacheConfigId',
  templateHash: 'templateHash',
  instanceId: 'instanceId',
  clusterId: 'clusterId',
  recommendationId: 'recommendationId',
};

export const dboAlerts = {
  deployCache: {
    title: 'Optimize for Peak Performance!',
    body: 'Unlock your database’s full potential with our advanced caching technology. Easily deploy a cache in your environment and effortlessly enable caching for faster query speeds and reduced load.',
    action: 'Deploy cache',
  },
  cacheOutdated: {
    title: 'Cache Component Outdated',
    body: 'A newer version of the DBO cache component is available. Until redeployed, the cache will continue running the current, outdated version.',
    action: 'Redeploy cache',
  },
  cacheRedeploymentNeeded: {
    title: 'Endpoint Configuration Changed',
    body: 'Endpoint configuration has been changed but is not yet applied to the running cache. Until redeployed, the cache will continue using the previous endpoint configuration.',
    action: 'Redeploy cache',
  },
  directModeActive: {
    title: 'DBO Paused',
    body: 'Queries are routing directly to the database. Status indicators may still reflect recent activity, but no processing or caching is occurring. Resume anytime to restore DB Optimizer caching and proxying.',
    action: 'Resume DBO',
  },
  connectionPoolingAvailable: {
    title: 'Connection Pooling Available',
    body: 'Your database instance is compatible with transaction-mode connection pooling. We recommend enabling it to reduce the number of database connections.',
    action: 'Enable connection pooling',
  },
  connectionPoolingAvailableButNotEligible: {
    title: 'Connection Pooling Available With Limitations',
    body: 'Your database instance uses features that may not be fully compatible with transaction-mode connection pooling. Review identified compatibility issues to learn more.',
    action: 'Review compatibility',
  },
};

export const dboTooltips = {
  // General metrics tooltips
  hitRate:
    'Percentage of database queries successfully served from the cache rather than being sent to the database.',
  projectedHitRate:
    'Projected percentage of database queries that would be served from the cache instead of being sent to the database.',
  cacheHits:
    'Database queries that were successfully retrieved from the cache instead of being executed directly on the database.',
  projectedCacheHits:
    'Projected number of queries that would be served from the cache instead of being executed directly on the database.',
  cacheMisses:
    "Queries that couldn't be served from the cache and required direct database execution.",
  readQueries:
    'Number of read queries (SELECT statements) processed by the database.',
  totalQueries:
    'Total number of database queries processed, including both cached responses and direct database queries.',
  uniqueParamSets:
    'Number of unique parameter sets for this query. Higher diversity can affect cache efficiency since each set may require separate cache entries.',
  uniqueResultSets: 'Number of unique result sets returned by this query.',

  // Performance metrics tooltips
  dbTimeSavedPercent:
    'Percentage of total database execution time saved through caching. Shows the performance improvement achieved by serving queries from cache instead of executing them on the database.',
  dbTimeSavedPercentQuery:
    'Percentage of total database execution time saved for each query through caching.',
  projectedDbTimeSavedPercent:
    'Projected percentage of total database execution time that would be eliminated through caching based on observed query patterns.',
  totalDbTime:
    'Total cumulative time spent executing queries directly on the database. This includes only queries that resulted in cache misses and required actual database processing.',
  totalDbTimeQuery:
    'Total cumulative time in seconds spent executing queries directly on the database. This includes only queries that resulted in cache misses and required actual database processing.',
  avgExecutionTime: 'Average execution time of all processed database queries.',
  avgHitExecutionTime:
    'Average time in milliseconds it takes to retrieve and serve a cached query response.',

  p50: 'Median time delay incurred when this query is not in the cache and must be fetched from the underlying database.',
  p95: 'Time delay incurred by the slowest 5% of cache misses for this query when fetched from the underlying database.',
  p99: 'Time delay incurred by the slowest 1% of cache misses for this query when fetched from the underlying database.',

  avgMissExecutionTime:
    'Average time in milliseconds it takes to process queries that miss the cache and must be executed against the database.',
  dbInstanceLoad:
    'Load measures the level of session activity in your database instance.',

  // Configuration tooltips
  cachedDbs:
    "The number of databases with caching capabilities enabled. This includes databases in both automatic and manual caching modes, regardless of whether they're actively caching data at this moment.",
  ttlDuration:
    'The duration a cached query remains valid before it expires and is removed from the cache. Set in seconds.',

  maxTtlDuration:
    'Highest time-to-live (TTL) value applied to cached results for this query during the selected time period.',
  minTtlDuration:
    'Lowest time-to-live (TTL) value applied to cached results for this query during the selected time period.',

  // Cost tooltips
  projectedSavings:
    'Savings that could be achieved by using the recommended database instances.',
  currentCost:
    'Sum of the monthly costs of all the current database instances.',
  estimatedCost:
    'Sum of the monthly costs of all the recommended database instances.',
  recommendedInstance:
    'The recommended database instance type based on your actual usage patterns. Rightsizing to this configuration can help achieve the potential savings shown.',

  // Connection metrics tooltips
  totalConnections:
    'Total open connections between your application and the database, including both active and idle.',
  activeConnections:
    'Connections currently executing a query or in an open transaction.',
  idleConnections:
    'Open connections not currently executing a query. These still consume memory and CPU.',

  // Proxy vs DB metrics tooltips
  appToDboConnections:
    'Connections from your application to DBO. With pooling, DBO accepts these and turns them into fewer database connections.',
  dboToDbConnections:
    'Connections from DBO to your database. With pooling, this should be lower than connections from your application to DBO.',
  connectionOptimization:
    'Reduction of application connections reaching your database from DBO connection pooling. ',
};
